import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { useAppContext } from '@/contexts/AppContext';
import { canEditTab } from '@/lib/permissions';
import {
  CalendarDays, ChevronDown, ChevronRight, ChevronLeft,
  Loader2, Download, CheckSquare, Square,
  CheckCircle, XCircle, AlertTriangle, Save, Pencil, X, Filter,
} from 'lucide-react';

import * as XLSX from 'xlsx';


/* ─── types ─── */
type ShiftRow = {
  id: string;
  driver_name: string;
  shift_date: string;
  start_time: string | null;
  end_time: string | null;
  break_time: number | null;
  vehicle_number: string | null;
  fleet: string | null;
  override_start_time: string | null;
  override_end_time: string | null;
};

/* ─── helpers ─── */
const pad2 = (n: number) => String(n).padStart(2, '0');

const fmtIsoLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const startOfWeekMon = (d: Date) => {
  const x = new Date(d);
  const dow = x.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfWeekSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

const fmtDateNZ = (iso: string) => {
  if (!iso) return '-';
  let s = iso;
  if (s.includes('T')) s = s.slice(0, 10);
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  return `${parts[2]}-${parts[1]}-${parts[0].slice(2)}`;
};

const fmtNZDateObj = (d: Date) => fmtDateNZ(fmtIsoLocal(d));

const fmtTimeHHmm = (t?: string | null) => {
  if (!t) return '-';
  return t.length >= 5 ? t.slice(0, 5) : t;
};

const dayNameShort = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
};

const minutesBetween = (date: string, start: string, end: string) => {
  const s = new Date(`${date}T${start}`);
  const e = new Date(`${date}T${end}`);
  let diff = Math.round((e.getTime() - s.getTime()) / 60000);
  if (diff < 0) diff += 24 * 60;
  return diff;
};

const fmtDuration = (mins: number) => {
  if (mins <= 0) return '0:00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${pad2(m)}`;
};

const fmtDecimalHours = (mins: number) => {
  return (mins / 60).toFixed(2);
};

/* Effective start/end considering overrides */
const effectiveStart = (s: ShiftRow) => s.override_start_time || s.start_time;
const effectiveEnd = (s: ShiftRow) => s.override_end_time || s.end_time;

/* Check if a shift is missing critical data */
const shiftIsMissingData = (s: ShiftRow): boolean => {
  const end = effectiveEnd(s);
  return !end || s.break_time === null || s.break_time === undefined || !s.vehicle_number;
};

/* What specifically is missing */
const getMissingFields = (s: ShiftRow): string[] => {
  const missing: string[] = [];
  const end = effectiveEnd(s);
  if (!end) missing.push('Finish Time');
  if (s.break_time === null || s.break_time === undefined) missing.push('Break Time');
  if (!s.vehicle_number) missing.push('Vehicle');
  return missing;
};

/* ─── grouped driver type ─── */
type DailyShift = {
  shift: ShiftRow;
  onlineMins: number;
  breakMins: number;
  onShiftMins: number;
  isMissing: boolean;
  missingFields: string[];
};

type DriverGroup = {
  driverName: string;
  paymentType: string | null;
  dailyShifts: DailyShift[];
  totalOnlineMins: number;
  totalBreakMins: number;
  totalOnShiftMins: number;
  shiftCount: number;
  hasMissingData: boolean;
  missingShiftCount: number;
};


/* ─── Inline edit state type ─── */
type EditState = {
  shiftId: string;
  startTime: string;
  endTime: string;
  breakTime: string;
  vehicleNumber: string;
};

/* ─── component ─── */
const TimesheetReport: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked driver(s) here, read-only unless
  // explicitly given edit rights. Unrestricted (the default -- everyone
  // today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "timesheets");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  // Week state (snaps Mon-Sun)
  const [anchorDate, setAnchorDate] = useState<Date>(() => startOfWeekMon(new Date()));
  const weekStart = useMemo(() => startOfWeekMon(anchorDate), [anchorDate]);
  const weekEnd = useMemo(() => endOfWeekSun(anchorDate), [anchorDate]);

  // Data
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [selectedDrivers, setSelectedDrivers] = useState<Set<string>>(new Set());
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  // Payment type filter
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('All');
  const [driverPaymentTypes, setDriverPaymentTypes] = useState<Map<string, string | null>>(new Map());

  // Inline editing
  const [editingShift, setEditingShift] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);


  // Week navigation
  const handleDatePick = (iso: string) => {
    if (!iso) return;
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (!Number.isNaN(date.getTime())) setAnchorDate(startOfWeekMon(date));
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setAnchorDate(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setAnchorDate(d);
  };

  // Fetch shifts for the week
  const fetchShifts = useCallback(async () => {
    const startIso = fmtIsoLocal(weekStart);
    const endIso = fmtIsoLocal(weekEnd);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('driver_shifts')
        .select('id,driver_name,shift_date,start_time,end_time,break_time,vehicle_number,fleet,override_start_time,override_end_time')
        .gte('shift_date', startIso)
        .lte('shift_date', endIso)
        .order('driver_name')
        .order('shift_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) {
        console.error('fetchShifts error:', error);
        setShifts([]);
        return;
      }
      setShifts(Array.isArray(data) ? (data as ShiftRow[]) : []);
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd]);

  useEffect(() => {
    fetchShifts();
    setExpandedDrivers(new Set());
    setSelectedDrivers(new Set());
    setEditingShift(null);
  }, [fetchShifts]);

  // Fetch driver payment types (once on mount)
  useEffect(() => {
    const fetchDriverPaymentTypes = async () => {
      const { data, error } = await supabase
        .from('drivers')
        .select('driver_name, payment_type');
      if (error) {
        console.error('Error fetching driver payment types:', error);
        return;
      }
      if (data) {
        const ptMap = new Map<string, string | null>();
        for (const d of data) {
          if (d.driver_name) {
            ptMap.set(d.driver_name.trim(), d.payment_type || null);
          }
        }
        setDriverPaymentTypes(ptMap);
      }
    };
    fetchDriverPaymentTypes();
  }, []);


  // Build driver groups
  const driverGroups: DriverGroup[] = useMemo(() => {
    const map = new Map<string, ShiftRow[]>();
    for (const s of shifts) {
      const name = (s.driver_name || '').trim();
      if (!name) continue;
      const arr = map.get(name) || [];
      arr.push(s);
      map.set(name, arr);
    }

    const groups: DriverGroup[] = [];
    for (const [driverName, driverShifts] of map.entries()) {
      let totalOnlineMins = 0;
      let totalBreakMins = 0;
      let missingShiftCount = 0;

      const dailyShifts: DailyShift[] = driverShifts.map((s) => {
        const start = effectiveStart(s);
        const end = effectiveEnd(s);
        const onlineMins = (start && end) ? minutesBetween(s.shift_date, start, end) : 0;
        const breakMins = typeof s.break_time === 'number' ? s.break_time : 0;
        const onShiftMins = Math.max(0, onlineMins - breakMins);
        const isMissing = shiftIsMissingData(s);
        const missingFields = getMissingFields(s);

        totalOnlineMins += onlineMins;
        totalBreakMins += breakMins;
        if (isMissing) missingShiftCount++;

        return { shift: s, onlineMins, breakMins, onShiftMins, isMissing, missingFields };
      });

      groups.push({
        driverName,
        paymentType: driverPaymentTypes.get(driverName) || null,
        dailyShifts,
        totalOnlineMins,
        totalBreakMins,
        totalOnShiftMins: Math.max(0, totalOnlineMins - totalBreakMins),
        shiftCount: driverShifts.length,
        hasMissingData: missingShiftCount > 0,
        missingShiftCount,
      });

    }

    groups.sort((a, b) => a.driverName.localeCompare(b.driverName));
    return groups;
  }, [shifts, driverPaymentTypes]);

  // Filtered groups (payment type + missing data filters)
  const filteredGroups = useMemo(() => {
    let result = driverGroups;
    if (scopedDriverNames) {
      const scoped = new Set(scopedDriverNames);
      result = result.filter(g => scoped.has(g.driverName));
    }
    if (paymentTypeFilter !== 'All') {
      result = result.filter(g => g.paymentType === paymentTypeFilter);
    }
    if (showMissingOnly) {
      result = result.filter(g => g.hasMissingData);
    }
    return result;
  }, [driverGroups, showMissingOnly, paymentTypeFilter, scopedDriverNames]);


  // Missing data stats
  const missingStats = useMemo(() => {
    let driversWithMissing = 0;
    let totalMissingShifts = 0;
    for (const g of driverGroups) {
      if (g.hasMissingData) {
        driversWithMissing++;
        totalMissingShifts += g.missingShiftCount;
      }
    }
    return { driversWithMissing, totalMissingShifts };
  }, [driverGroups]);

  // Grand totals (for selected drivers only, or all if none selected)
  const grandTotals = useMemo(() => {
    const relevantGroups = selectedDrivers.size > 0
      ? filteredGroups.filter(g => selectedDrivers.has(g.driverName))
      : filteredGroups;
    let online = 0, breaks = 0, onShift = 0;
    for (const g of relevantGroups) {
      online += g.totalOnlineMins;
      breaks += g.totalBreakMins;
      onShift += g.totalOnShiftMins;
    }
    return { online, breaks, onShift };
  }, [filteredGroups, selectedDrivers]);

  // Toggle expand
  const toggleExpand = (name: string) => {
    setExpandedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Toggle select
  const toggleSelect = (name: string) => {
    setSelectedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Select all / deselect all
  const selectAll = () => {
    setSelectedDrivers(new Set(filteredGroups.map(g => g.driverName)));
  };

  const deselectAll = () => {
    setSelectedDrivers(new Set());
  };

  // ─── INLINE EDITING ───
  const startEditing = (shift: ShiftRow) => {
    const start = effectiveStart(shift);
    const end = effectiveEnd(shift);
    setEditingShift({
      shiftId: shift.id,
      startTime: start ? fmtTimeHHmm(start) : '',
      endTime: end ? fmtTimeHHmm(end) : '',
      breakTime: shift.break_time !== null && shift.break_time !== undefined ? String(shift.break_time) : '',
      vehicleNumber: shift.vehicle_number || '',
    });
  };

  const cancelEditing = () => {
    setEditingShift(null);
  };

  const saveEdit = async () => {
    if (!editingShift || !canEdit) return;
    setSaving(true);

    try {
      const updates: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      // Find the original shift
      const originalShift = shifts.find(s => s.id === editingShift.shiftId);
      if (!originalShift) return;

      // ── Start Time ──
      if (editingShift.startTime && editingShift.startTime.trim()) {
        let timeVal = editingShift.startTime.trim();
        if (timeVal.length === 5) timeVal += ':00';
        if (!originalShift.start_time) {
          updates.start_time = timeVal;
        } else if (!originalShift.override_start_time && originalShift.start_time) {
          const origStart = fmtTimeHHmm(originalShift.start_time);
          if (editingShift.startTime.trim() !== origStart) {
            updates.override_start_time = timeVal;
            updates.override_reason = 'Admin correction via timesheet';
            updates.override_by = 'Admin';
            updates.override_at = new Date().toISOString();
          }
        } else {
          const origOverride = fmtTimeHHmm(originalShift.override_start_time);
          if (editingShift.startTime.trim() !== origOverride) {
            updates.override_start_time = timeVal;
          }
        }
      }

      // ── End Time ──
      if (editingShift.endTime && editingShift.endTime.trim()) {
        let timeVal = editingShift.endTime.trim();
        if (timeVal.length === 5) timeVal += ':00';
        if (!originalShift.end_time) {
          updates.end_time = timeVal;
        } else if (!originalShift.override_end_time && originalShift.end_time) {
          const origEnd = fmtTimeHHmm(originalShift.end_time);
          if (editingShift.endTime.trim() !== origEnd) {
            updates.override_end_time = timeVal;
            if (!updates.override_reason) {
              updates.override_reason = 'Admin correction via timesheet';
              updates.override_by = 'Admin';
              updates.override_at = new Date().toISOString();
            }
          }
        } else {
          const origOverride = fmtTimeHHmm(originalShift.override_end_time);
          if (editingShift.endTime.trim() !== origOverride) {
            updates.override_end_time = timeVal;
          }
        }
      }

      // ── Break Time ──
      if (editingShift.breakTime !== '' && editingShift.breakTime !== null) {
        const breakVal = parseInt(editingShift.breakTime, 10);
        if (!isNaN(breakVal) && breakVal >= 0) {
          updates.break_time = breakVal;
        }
      }

      // ── Vehicle Number ──
      if (editingShift.vehicleNumber.trim()) {
        updates.vehicle_number = editingShift.vehicleNumber.trim();
      }

      const { error } = await supabase
        .from('driver_shifts')
        .update(updates)
        .eq('id', editingShift.shiftId);

      if (error) {
        console.error('Error saving shift:', error);
        alert('Failed to save: ' + error.message);
        return;
      }

      // Update local state immediately for responsiveness
      setShifts(prev => prev.map(s => {
        if (s.id !== editingShift.shiftId) return s;
        const updated = { ...s };
        if (updates.start_time) updated.start_time = updates.start_time;
        if (updates.override_start_time) updated.override_start_time = updates.override_start_time;
        if (updates.end_time) updated.end_time = updates.end_time;
        if (updates.override_end_time) updated.override_end_time = updates.override_end_time;
        if (updates.break_time !== undefined) updated.break_time = updates.break_time;
        if (updates.vehicle_number) updated.vehicle_number = updates.vehicle_number;
        return updated;
      }));

      setEditingShift(null);
    } finally {
      setSaving(false);
    }
  };


  // ─── EXPORT ───
  const exportTimesheet = () => {
    const driversToExport = filteredGroups.filter(g => selectedDrivers.has(g.driverName));
    if (driversToExport.length === 0) return;

    const wb = XLSX.utils.book_new();

    // Build all days of the week (Mon-Sun)
    const weekDays: string[] = [];
    const d = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      weekDays.push(fmtIsoLocal(d));
      d.setDate(d.getDate() + 1);
    }

    // Summary sheet - one row per driver per day + weekly total
    const summaryRows: any[] = [];

    for (const group of driversToExport) {
      const dayMap = new Map<string, { start: string; end: string; breakMins: number; onlineMins: number; onShiftMins: number; vehicle: string }>();

      for (const ds of group.dailyShifts) {
        const dateKey = ds.shift.shift_date;
        const existing = dayMap.get(dateKey);
        const start = effectiveStart(ds.shift);
        const end = effectiveEnd(ds.shift);

        if (existing) {
          if (start && (!existing.start || start < existing.start)) existing.start = start;
          if (end && (!existing.end || end > existing.end)) existing.end = end;
          existing.breakMins += ds.breakMins;
          existing.onlineMins += ds.onlineMins;
          existing.onShiftMins += ds.onShiftMins;
          if (ds.shift.vehicle_number && !existing.vehicle) existing.vehicle = ds.shift.vehicle_number;
        } else {
          dayMap.set(dateKey, {
            start: start || '',
            end: end || '',
            breakMins: ds.breakMins,
            onlineMins: ds.onlineMins,
            onShiftMins: ds.onShiftMins,
            vehicle: ds.shift.vehicle_number || '',
          });
        }
      }

      for (const dayIso of weekDays) {
        const dayData = dayMap.get(dayIso);
        const dayLabel = dayNameShort(dayIso);
        const dateLabel = fmtDateNZ(dayIso);

        if (dayData) {
          summaryRows.push({
            'Driver': group.driverName,
            'Day': dayLabel,
            'Date': dateLabel,
            'Start': fmtTimeHHmm(dayData.start),
            'Finish': fmtTimeHHmm(dayData.end),
            'Break (mins)': dayData.breakMins,
            'Online (hrs)': fmtDecimalHours(dayData.onlineMins),
            'On Shift (hrs)': fmtDecimalHours(dayData.onShiftMins),
            'Vehicle': dayData.vehicle,
          });
        } else {
          summaryRows.push({
            'Driver': group.driverName,
            'Day': dayLabel,
            'Date': dateLabel,
            'Start': '',
            'Finish': '',
            'Break (mins)': '',
            'Online (hrs)': '',
            'On Shift (hrs)': '',
            'Vehicle': '',
          });
        }
      }

      summaryRows.push({
        'Driver': group.driverName,
        'Day': '',
        'Date': 'WEEK TOTAL',
        'Start': '',
        'Finish': '',
        'Break (mins)': group.totalBreakMins,
        'Online (hrs)': fmtDecimalHours(group.totalOnlineMins),
        'On Shift (hrs)': fmtDecimalHours(group.totalOnShiftMins),
        'Vehicle': '',
      });

      summaryRows.push({
        'Driver': '', 'Day': '', 'Date': '', 'Start': '', 'Finish': '',
        'Break (mins)': '', 'Online (hrs)': '', 'On Shift (hrs)': '', 'Vehicle': '',
      });
    }

    const ws = XLSX.utils.json_to_sheet(summaryRows);

    ws['!cols'] = [
      { wch: 25 },
      { wch: 6 },
      { wch: 12 },
      { wch: 8 },
      { wch: 8 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 10 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');

    const weekLabel = `${fmtDateNZ(fmtIsoLocal(weekStart))}_to_${fmtDateNZ(fmtIsoLocal(weekEnd))}`.replace(/\//g, '-');
    XLSX.writeFile(wb, `Timesheet_${weekLabel}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Week Picker */}
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader>
          <CardTitle className="text-xl text-primary flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Driver Timesheet Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-end gap-3">
            <div>
              <label className="block text-sm mb-1">Week of (snaps to Monday)</label>
              <Input
                type="date"
                value={fmtIsoLocal(weekStart)}
                onChange={(e) => handleDatePick(e.target.value)}
                className="dark-input"
              />
            </div>

            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={prevWeek} className="border-border">
                <ChevronLeft className="w-4 h-4 mr-1" />
              </Button>
              <Button variant="outline" onClick={nextWeek} className="border-border">
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>

              <Button onClick={fetchShifts} className="golden-button">
                Reload
              </Button>
            </div>

            <div>
              <label className="block text-sm mb-1">Payment Type</label>
              <Select value={paymentTypeFilter} onValueChange={(val) => { setPaymentTypeFilter(val); setSelectedDrivers(new Set()); }}>
                <SelectTrigger className="w-[160px] border-border">
                  <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Types</SelectItem>
                  <SelectItem value="Waged">Waged</SelectItem>
                  <SelectItem value="Commission">Commission</SelectItem>
                  <SelectItem value="Contractor">Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>


            <div className="ml-auto flex items-center gap-3">
              <Badge variant="secondary">
                Week: {fmtNZDateObj(weekStart)} – {fmtNZDateObj(weekEnd)}
              </Badge>
              <Badge variant="outline">
                {driverGroups.length} driver{driverGroups.length !== 1 ? 's' : ''} with shifts
              </Badge>
            </div>
          </div>

          {loading && (
            <div className="mt-4 flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading shifts…
            </div>
          )}
        </CardContent>
      </Card>

      {/* Missing Data Banner */}
      {!loading && missingStats.driversWithMissing > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-semibold text-amber-600 dark:text-amber-400">
                    Missing Data Detected
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {missingStats.driversWithMissing} driver{missingStats.driversWithMissing !== 1 ? 's' : ''} with {missingStats.totalMissingShifts} incomplete shift{missingStats.totalMissingShifts !== 1 ? 's' : ''} — missing finish time, break time, or vehicle
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                  Show Missing Only
                </label>
                <Switch
                  checked={showMissingOnly}
                  onCheckedChange={setShowMissingOnly}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      {!loading && filteredGroups.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{fmtDuration(grandTotals.online)}</div>
              <p className="text-sm text-muted-foreground">
                Total Online {selectedDrivers.size > 0 ? `(${selectedDrivers.size} selected)` : '(all)'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{fmtDuration(grandTotals.breaks)}</div>
              <p className="text-sm text-muted-foreground">Total Breaks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{fmtDuration(grandTotals.onShift)}</div>
              <p className="text-sm text-muted-foreground">Total On Shift</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Selection controls + Export */}
      {!loading && filteredGroups.length > 0 && (
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={selectAll} className="border-border">
            <CheckSquare className="w-4 h-4 mr-1" />
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={deselectAll} className="border-border">
            <Square className="w-4 h-4 mr-1" />
            Deselect All
          </Button>

          {selectedDrivers.size > 0 && (
            <span className="text-sm text-muted-foreground">
              {selectedDrivers.size} driver{selectedDrivers.size !== 1 ? 's' : ''} selected
            </span>
          )}

          <div className="ml-auto">
            <Button
              onClick={exportTimesheet}
              disabled={selectedDrivers.size === 0}
              className="golden-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Timesheet ({selectedDrivers.size})
            </Button>
          </div>
        </div>
      )}

      {/* Driver table */}
      {!loading && filteredGroups.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 text-center"></TableHead>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Shifts</TableHead>
                  <TableHead className="text-center">Total Online</TableHead>
                  <TableHead className="text-center">Total Breaks</TableHead>
                  <TableHead className="text-center">Total On Shift</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGroups.map((group) => {
                  const isExpanded = expandedDrivers.has(group.driverName);
                  const isSelected = selectedDrivers.has(group.driverName);

                  return (
                    <React.Fragment key={group.driverName}>
                      <TableRow
                        className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/10' : ''} ${group.hasMissingData ? 'border-l-2 border-l-red-500' : 'border-l-2 border-l-green-500'}`}
                      >
                        <TableCell className="w-10 text-center" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(group.driverName)}
                          />
                        </TableCell>
                        <TableCell
                          className="w-8 px-2"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />
                          }
                        </TableCell>
                        <TableCell
                          className="font-semibold"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          <div className="flex items-center gap-2">
                            {group.driverName}
                            {group.hasMissingData ? (
                              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            )}
                            {group.paymentType && (
                              <Badge
                                variant="outline"
                                className={`text-[10px] px-1.5 py-0 h-5 flex-shrink-0 ${
                                  group.paymentType === 'Waged'
                                    ? 'border-blue-500/50 text-blue-400 bg-blue-500/10'
                                    : group.paymentType === 'Commission'
                                    ? 'border-purple-500/50 text-purple-400 bg-purple-500/10'
                                    : group.paymentType === 'Contractor'
                                    ? 'border-orange-500/50 text-orange-400 bg-orange-500/10'
                                    : 'border-border'
                                }`}
                              >
                                {group.paymentType}
                              </Badge>
                            )}
                          </div>

                        </TableCell>
                        <TableCell
                          className="text-center"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {group.hasMissingData ? (
                            <Badge variant="destructive" className="text-xs">
                              {group.missingShiftCount} incomplete
                            </Badge>
                          ) : (
                            <Badge className="text-xs bg-green-600 hover:bg-green-700 text-white">
                              Complete
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell
                          className="text-center"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {group.shiftCount}
                        </TableCell>
                        <TableCell
                          className="text-center"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {fmtDuration(group.totalOnlineMins)}
                        </TableCell>
                        <TableCell
                          className="text-center"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {group.totalBreakMins} min
                        </TableCell>
                        <TableCell
                          className="text-center font-semibold"
                          onClick={() => toggleExpand(group.driverName)}
                        >
                          {fmtDuration(group.totalOnShiftMins)}
                          <span className="text-xs text-muted-foreground ml-2">
                            ({fmtDecimalHours(group.totalOnShiftMins)} hrs)
                          </span>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={9} className="p-0 bg-muted/30">

                            <div className="px-6 py-2">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead>Day</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Start</TableHead>
                                    <TableHead>Finish</TableHead>
                                    <TableHead>Breaks</TableHead>
                                    <TableHead>Online</TableHead>
                                    <TableHead>On Shift</TableHead>
                                    <TableHead>Vehicle</TableHead>
                                    <TableHead className="w-20">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {group.dailyShifts.map((ds) => {
                                    const start = effectiveStart(ds.shift);
                                    const end = effectiveEnd(ds.shift);
                                    const isEditing = editingShift?.shiftId === ds.shift.id;
                                    const rowMissing = ds.isMissing;

                                    return (
                                      <TableRow
                                        key={ds.shift.id}
                                        className={rowMissing ? 'bg-red-500/8 hover:bg-red-500/15' : ''}
                                      >
                                        {/* Warning icon */}
                                        <TableCell className="w-8 px-1">
                                          {rowMissing && (
                                            <div className="relative group">
                                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                                              <div className="absolute left-6 top-0 z-50 hidden group-hover:block bg-popover border border-border rounded-md shadow-lg p-2 text-xs whitespace-nowrap">
                                                Missing: {ds.missingFields.join(', ')}
                                              </div>
                                            </div>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-xs">{dayNameShort(ds.shift.shift_date)}</TableCell>
                                        <TableCell>{fmtDateNZ(ds.shift.shift_date)}</TableCell>

                                        {/* Start Time - editable */}
                                        <TableCell>
                                          {isEditing ? (
                                            <Input
                                              type="time"
                                              value={editingShift.startTime}
                                              onChange={(e) => setEditingShift(prev => prev ? { ...prev, startTime: e.target.value } : null)}
                                              className="h-7 w-28 text-xs"
                                            />
                                          ) : (
                                            start ? (
                                              fmtTimeHHmm(start)
                                            ) : (
                                              <span className="text-red-500 font-medium flex items-center gap-1">
                                                <XCircle className="w-3 h-3" />
                                                Missing
                                              </span>
                                            )
                                          )}
                                        </TableCell>

                                        {/* Finish Time - editable */}
                                        <TableCell>
                                          {isEditing ? (
                                            <Input
                                              type="time"
                                              value={editingShift.endTime}
                                              onChange={(e) => setEditingShift(prev => prev ? { ...prev, endTime: e.target.value } : null)}
                                              className="h-7 w-28 text-xs"
                                            />
                                          ) : (
                                            end ? (
                                              fmtTimeHHmm(end)
                                            ) : (
                                              <span className="text-red-500 font-medium flex items-center gap-1">
                                                <XCircle className="w-3 h-3" />
                                                Missing
                                              </span>
                                            )
                                          )}
                                        </TableCell>

                                        {/* Break Time - editable */}
                                        <TableCell>
                                          {isEditing ? (
                                            <div className="flex items-center gap-1">
                                              <Input
                                                type="number"
                                                min="0"
                                                value={editingShift.breakTime}
                                                onChange={(e) => setEditingShift(prev => prev ? { ...prev, breakTime: e.target.value } : null)}
                                                className="h-7 w-16 text-xs"
                                                placeholder="mins"
                                              />
                                              <span className="text-xs text-muted-foreground">min</span>
                                            </div>
                                          ) : (
                                            ds.shift.break_time !== null && ds.shift.break_time !== undefined ? (
                                              `${ds.breakMins} min`
                                            ) : (
                                              <span className="text-red-500 font-medium flex items-center gap-1">
                                                <XCircle className="w-3 h-3" />
                                                Missing
                                              </span>
                                            )
                                          )}
                                        </TableCell>

                                        <TableCell>{(start && end) ? fmtDuration(ds.onlineMins) : '-'}</TableCell>
                                        <TableCell className="font-semibold">
                                          {(start && end) ? fmtDuration(ds.onShiftMins) : '-'}
                                        </TableCell>

                                        {/* Vehicle - editable */}
                                        <TableCell>
                                          {isEditing ? (
                                            <Input
                                              type="text"
                                              value={editingShift.vehicleNumber}
                                              onChange={(e) => setEditingShift(prev => prev ? { ...prev, vehicleNumber: e.target.value } : null)}
                                              className="h-7 w-20 text-xs"
                                              placeholder="Vehicle #"
                                            />
                                          ) : (
                                            ds.shift.vehicle_number ? (
                                              ds.shift.vehicle_number
                                            ) : (
                                              <span className="text-red-500 font-medium flex items-center gap-1">
                                                <XCircle className="w-3 h-3" />
                                                Missing
                                              </span>
                                            )
                                          )}
                                        </TableCell>

                                        {/* Actions - Edit button on ALL rows */}
                                        <TableCell>
                                          {!isEditing && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className={`h-7 px-2 text-xs ${rowMissing ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-500/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                                              onClick={() => startEditing(ds.shift)}
                                            >
                                              <Pencil className="w-3 h-3 mr-1" />
                                              Edit
                                            </Button>
                                          )}
                                          {isEditing && (
                                            <div className="flex items-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-500/10"
                                                onClick={saveEdit}
                                                disabled={saving}
                                              >
                                                {saving ? (
                                                  <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <Save className="w-3 h-3" />
                                                )}
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                                onClick={cancelEditing}
                                                disabled={saving}
                                              >
                                                <X className="w-3 h-3" />
                                              </Button>
                                            </div>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}

                                  <TableRow className="border-t-2">
                                    <TableCell></TableCell>
                                    <TableCell colSpan={4} className="font-semibold text-right">
                                      Week Totals:
                                    </TableCell>
                                    <TableCell className="font-semibold">{group.totalBreakMins} min</TableCell>
                                    <TableCell className="font-semibold">{fmtDuration(group.totalOnlineMins)}</TableCell>
                                    <TableCell className="font-bold">
                                      {fmtDuration(group.totalOnShiftMins)}
                                      <span className="text-xs text-muted-foreground ml-1">
                                        ({fmtDecimalHours(group.totalOnShiftMins)} hrs)
                                      </span>
                                    </TableCell>
                                    <TableCell colSpan={2}></TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && filteredGroups.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            {(showMissingOnly || paymentTypeFilter !== 'All') && driverGroups.length > 0 ? (
              <div>
                {showMissingOnly && !driverGroups.some(g => g.hasMissingData) ? (
                  <>
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">All Shifts Complete</p>
                    <p className="text-muted-foreground mt-1">No drivers with missing data this week.</p>
                  </>
                ) : (
                  <>
                    <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-lg font-semibold">No Matching Drivers</p>
                    <p className="text-muted-foreground mt-1">
                      No drivers found for the current filters
                      {paymentTypeFilter !== 'All' ? ` (${paymentTypeFilter})` : ''}
                      {showMissingOnly ? ' with missing data' : ''}.
                    </p>
                  </>
                )}
                <div className="flex items-center justify-center gap-2 mt-3">
                  {paymentTypeFilter !== 'All' && (
                    <Button variant="outline" size="sm" onClick={() => setPaymentTypeFilter('All')}>
                      Clear Type Filter
                    </Button>
                  )}
                  {showMissingOnly && (
                    <Button variant="outline" size="sm" onClick={() => setShowMissingOnly(false)}>
                      Show All Drivers
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No shifts found for this week.</p>
            )}
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default TimesheetReport;
